/* ============================================================================
 * Project        : Deloitte Digital Compliance
 * File           : CustomDateRangePicker.jsx
 * Version        : 1.0.0
 * Author         : Team Neubinary
 * Created On     : 2026-06-24
 *
 * Description    : Digital Compliance for Automation and Governance
 *
 * Change History :
 * ----------------------------------------------------------------------------
 * Date         | Version | Author           | Description
 * ----------------------------------------------------------------------------
 * 2026-06-24   | 1.0.0   | Team Neubinary   | Initial creation
 * <YYYY-MM-DD> | x.x.x   | <Name/Team>      | <Change description>
 * ============================================================================
 *
 * Copyright (c) 2026 AU79 Consulting Services Pvt. Ltd.
 * All Rights Reserved.
 *
 * This source code and its contents are confidential and proprietary to
 * AU79 Consulting Services Pvt. Ltd. Unauthorized copying, reproduction,
 * modification, distribution, or disclosure of this material, in whole or
 * in part, is strictly prohibited without prior written permission from
 * AU79 Consulting Services Pvt. Ltd.
 * ============================================================================
 */
import React, { useState } from "react";
import PropTypes from "prop-types";
import {
  Box,
  Typography,
  TextField,
  IconButton,
  Popover,
  Divider,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { StaticDatePicker } from "@mui/x-date-pickers/StaticDatePicker";
import { AdapterMoment } from "@mui/x-date-pickers/AdapterMoment";

export default function CustomDateRangePicker({
  label = "",
  logsDateRange,
  setLogsDateRange,
  filteredLogs, // 👈 Passed from parent
}) {
  const [anchorEl, setAnchorEl] = useState(null);

  const compactDatePickerSx = {
    width: "120px !important",
    height: "250px !important",
    "& .MuiPickersLayout-root": {
      minWidth: "0 !important",
      maxWidth: "120px !important",
    },
    "& .MuiPickersLayout-contentWrapper": {
      padding: 0,
    },
    "& .MuiPickersCalendarHeader-switchViewButton": {
      display: "none",
    },
    "& .MuiPickersCalendarHeader-root": {
      position: "relative",
      paddingLeft: "1px",
      paddingRight: "1px",
      justifyContent: "space-between",
      minHeight: "20px",
      marginBottom: 0,
    },
    "& .MuiPickersCalendarHeader-labelContainer": {
      position: "absolute",
      left: "50%",
      transform: "translateX(-50%)",
    },
    "& .MuiPickersCalendarHeader-label": {
      fontSize: "14px",
    },
    "& .MuiPickersArrowSwitcher-root": {
      width: "100%",
      display: "flex",
      justifyContent: "space-between",
    },
    "& .MuiPickersArrowSwitcher-spacer": {
      display: "none",
    },
    "& .MuiPickersArrowSwitcher-button": {
      padding: 0,
    },
    "& .MuiDayCalendar-header": {
      gap: "0px",
      justifyContent: "space-between",
    },
    "& .MuiDayCalendar-weekContainer": {
      margin: "0px",
      justifyContent: "space-between",
    },
    "& .MuiPickersDay-root": {
      fontSize: "12px",
      width: "70px",
      height: "30px",
      margin: "0px",
      borderRadius: "0px !important",
    },
    "& .MuiDayCalendar-weekDayLabel": {
      fontSize: "12px",
      width: "70px",
      height: "30px",
      margin: "0px",
      borderRadius: "0px !important",
    },
    "& .MuiPickersDay-root.Mui-selected": {
      backgroundColor: "#000000",
      color: "#FFFFFF",
      "&:hover": {
        backgroundColor: "#000000",
      },
      borderRadius: "0px !important",
    },
    "& .MuiPickersDay-root:not(.Mui-selected)": {
      borderRadius: "0px !important",
    },
    "& .MuiPickersYear-yearButton.Mui-selected": {
      backgroundColor: "#000000 !important",
      color: "#FFFFFF !important",
      "&:hover": {
        backgroundColor: "#000000 !important",
      },
      borderRadius: "0px !important",
    },
    "& .MuiPickersMonth-monthButton.Mui-selected": {
      backgroundColor: "#000000 !important",
      color: "#FFFFFF !important",
      "&:hover": {
        backgroundColor: "#000000 !important",
      },
      borderRadius: "0px !important",
    },
  };

  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: 1, mr: 4 }}>
      <Typography sx={{ fontSize: "14px", color: "#53565A" }}>
        {label}
      </Typography>

      <TextField
        size="small"
        placeholder="MM/DD/YYYY — MM/DD/YYYY"
        value={
          logsDateRange[0] && logsDateRange[1]
            ? `${logsDateRange[0].format(
              "MM/DD/YYYY"
            )} — ${logsDateRange[1].format("MM/DD/YYYY")}`
            : ""
        }
        onClick={(e) => setAnchorEl(e.currentTarget)}
        InputProps={{
          readOnly: true,
          endAdornment: (
            <Box sx={{ display: "flex", alignItems: "center" }}>
              {(logsDateRange[0] || logsDateRange[1]) && (
                <IconButton
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation();
                    setLogsDateRange([null, null]);
                  }}
                >
                  <CloseIcon sx={{ fontSize: 16, color: "#000" }} />
                </IconButton>
              )}
              <CalendarMonthIcon
                sx={{ fontSize: 18, color: "#000", cursor: "pointer" }}
              />
            </Box>
          ),
        }}
        sx={{
          width: "235px",
          cursor: "pointer",
          "& .MuiOutlinedInput-root": {
            fontSize: "13px",
            "& fieldset": { borderColor: "#000", borderRadius: "0px" },
            "&:hover fieldset": { borderColor: "#000" },
            "&.Mui-focused fieldset": { borderColor: "#000" },
          },
        }}
      />

      <Popover
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
        slotProps={{ paper: { sx: { overflow: "visible", borderRadius: "0px" } } }}
      >
        <LocalizationProvider dateAdapter={AdapterMoment}>
          <Box sx={{ display: "flex", p: 0 }}>
            <StaticDatePicker
              displayStaticWrapperAs="desktop"
              value={logsDateRange[0]}
              onChange={(newValue) => {
                setLogsDateRange([newValue, logsDateRange[1]]);
                if (newValue && logsDateRange[1]) setAnchorEl(null); // auto close
              }}
              maxDate={logsDateRange[1]}
              slotProps={{ actionBar: { actions: [] } }}
              sx={compactDatePickerSx}
            />

            <Divider orientation="vertical" flexItem sx={{ mt: "16px" }} />

            <StaticDatePicker
              displayStaticWrapperAs="desktop"
              value={logsDateRange[1]}
              onChange={(newValue) => {
                setLogsDateRange([logsDateRange[0], newValue]);
                if (logsDateRange[0] && newValue) setAnchorEl(null); // auto close
              }}
              minDate={logsDateRange[0]}
              slotProps={{ actionBar: { actions: [] } }}
              sx={compactDatePickerSx}
            />
          </Box>
        </LocalizationProvider>
      </Popover>
    </Box>
  );
}

CustomDateRangePicker.propTypes = {
  label: PropTypes.string,
  logsDateRange: PropTypes.array,
  setLogsDateRange: PropTypes.func,
  filteredLogs: PropTypes.array,
};
